import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { merchantApi } from '../api/config';

// ============================================
// Theme Settings Interface
// ============================================
export interface ThemeSettings {
  primary_color: string;
  primary_hover_color: string;
  secondary_color: string;
  accent_color: string;
  bg_main_color: string;
  bg_sidebar_color: string;
  text_primary_color: string;
  text_secondary_color: string;
  border_radius: string;
  theme_mode: 'light' | 'dark';
}

export interface SystemSettings {
  system_name?: string;
  system_logo_url?: string;
}

interface ThemeContextType {
  theme: ThemeSettings;
  systemSettings: SystemSettings;
  loading: boolean;
  error: string | null;
  updateTheme: (newSettings: Partial<ThemeSettings>) => Promise<void>;
  refreshTheme: () => Promise<void>;
  applyTheme: (settings: ThemeSettings) => void;
  toggleTheme: () => void;
  isDarkMode: boolean;
}

// Default Theme Settings
const defaultTheme: ThemeSettings = {
  primary_color: '#ff6b35',
  primary_hover_color: '#e65a2a',
  secondary_color: '#f7931e',
  accent_color: '#667eea',
  bg_main_color: '#f5f7fa',
  bg_sidebar_color: '#1a1a2e',
  text_primary_color: '#1f2937',
  text_secondary_color: '#6b7280',
  border_radius: '0.5rem',
  theme_mode: 'light',
};

// ============================================
// Theme Context
// ============================================
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================
// Helper Functions
// ============================================

/**
 * แปลง Hex Color เป็น rgba
 */
function hexToRgba(hex: string, alpha: number = 0.1): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
}

/**
 * ทำให้สีเข้มขึ้นสำหรับ hover state
 */
function darkenColor(hex: string, percent: number = 10): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max((num >> 16) - amt, 0);
  const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
  const B = Math.max((num & 0x0000ff) - amt, 0);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

/**
 * Apply theme settings to CSS variables
 */
function applyCSSVariables(settings: ThemeSettings): void {
  const root = document.documentElement;
  
  // Primary Colors
  root.style.setProperty('--theme-primary', settings.primary_color);
  root.style.setProperty('--theme-primary-hover', settings.primary_hover_color || darkenColor(settings.primary_color));
  root.style.setProperty('--theme-primary-light', hexToRgba(settings.primary_color, 0.1));
  root.style.setProperty('--theme-primary-gradient', 
    `linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%)`
  );
  
  // Secondary Colors
  root.style.setProperty('--theme-secondary', settings.secondary_color);
  root.style.setProperty('--theme-secondary-hover', darkenColor(settings.secondary_color));
  root.style.setProperty('--theme-secondary-gradient',
    `linear-gradient(135deg, ${settings.secondary_color} 0%, ${darkenColor(settings.secondary_color, 18)} 100%)`
  );
  
  // Accent Colors
  root.style.setProperty('--theme-accent', settings.accent_color);
  root.style.setProperty('--theme-accent-hover', darkenColor(settings.accent_color));
  root.style.setProperty('--theme-accent-gradient',
    `linear-gradient(135deg, ${settings.accent_color} 0%, ${darkenColor(settings.accent_color, 18)} 100%)`
  );
  
  // Background Colors
  root.style.setProperty('--theme-bg-main', settings.bg_main_color);
  root.style.setProperty('--theme-bg-sidebar', settings.bg_sidebar_color);
  root.style.setProperty('--theme-bg-sidebar-hover', darkenColor(settings.bg_sidebar_color, -10));
  
  // Text Colors
  root.style.setProperty('--theme-text-primary', settings.text_primary_color);
  root.style.setProperty('--theme-text-secondary', settings.text_secondary_color);
  
  // Border Radius
  root.style.setProperty('--theme-radius', settings.border_radius);
  root.style.setProperty('--theme-radius-sm', `calc(${settings.border_radius} * 0.75)`);
  root.style.setProperty('--theme-radius-lg', `calc(${settings.border_radius} * 1.5)`);
  root.style.setProperty('--theme-radius-xl', `calc(${settings.border_radius} * 2)`);
  
  // Shadow with primary color
  root.style.setProperty('--theme-shadow-primary', 
    `0 4px 14px 0 ${hexToRgba(settings.primary_color, 0.39)}`
  );
  
  // Theme Mode
  if (settings.theme_mode === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Parse theme settings from API response
 */
function parseThemeFromAPI(data: Array<{ setting_key: string; setting_value: string }>): Partial<ThemeSettings> {
  const settings: Partial<ThemeSettings> = {};
  
  data.forEach(item => {
    switch (item.setting_key) {
      case 'theme_primary_color':
        settings.primary_color = item.setting_value;
        break;
      case 'theme_primary_hover_color':
        settings.primary_hover_color = item.setting_value;
        break;
      case 'theme_secondary_color':
        settings.secondary_color = item.setting_value;
        break;
      case 'theme_accent_color':
        settings.accent_color = item.setting_value;
        break;
      case 'theme_bg_main_color':
        settings.bg_main_color = item.setting_value;
        break;
      case 'theme_bg_sidebar_color':
        settings.bg_sidebar_color = item.setting_value;
        break;
      case 'theme_text_primary_color':
        settings.text_primary_color = item.setting_value;
        break;
      case 'theme_text_secondary_color':
        settings.text_secondary_color = item.setting_value;
        break;
      case 'theme_border_radius':
        settings.border_radius = item.setting_value;
        break;
      case 'theme_mode':
        settings.theme_mode = item.setting_value as 'light' | 'dark';
        break;
    }
  });
  
  return settings;
}

// ============================================
// Theme Provider Component
// ============================================
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeSettings>(defaultTheme);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    system_name: 'Go2Pay',
    system_logo_url: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to avoid circular dependency between applyTheme and toggleTheme
  const themeRef = useRef(theme);
  themeRef.current = theme;

  // Apply theme to CSS variables
  const applyTheme = useCallback((settings: ThemeSettings) => {
    applyCSSVariables(settings);
    setTheme(settings);
  }, []);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    const currentTheme = themeRef.current;
    const newMode: 'light' | 'dark' = currentTheme.theme_mode === 'dark' ? 'light' : 'dark';
    
    // Define colors for each mode
    const darkColors = {
      bg_main_color: '#0f172a',
      text_primary_color: '#f1f5f9',
      text_secondary_color: '#94a3b8',
    };
    
    const lightColors = {
      bg_main_color: '#f5f7fa',
      text_primary_color: '#1f2937',
      text_secondary_color: '#6b7280',
    };
    
    const colorOverrides = newMode === 'dark' ? darkColors : lightColors;
    
    const newTheme: ThemeSettings = { 
      ...currentTheme, 
      theme_mode: newMode,
      ...colorOverrides
    };
    
    applyTheme(newTheme);
    
    // Save preference to localStorage
    localStorage.setItem('theme_mode', newMode);
  }, [applyTheme]);

  // Dark mode state
  const isDarkMode = theme.theme_mode === 'dark';

  // Fetch theme from API
  const refreshTheme = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const savedMode = localStorage.getItem('theme_mode') as 'light' | 'dark' | null;
      
      // Define colors for each mode
      const darkColors = {
        bg_main_color: '#0f172a',
        text_primary_color: '#f1f5f9',
        text_secondary_color: '#94a3b8',
      };
      
      const lightColors = {
        bg_main_color: '#f5f7fa',
        text_primary_color: '#1f2937',
        text_secondary_color: '#6b7280',
      };
      
      if (savedMode) {
        const colorOverrides = savedMode === 'dark' ? darkColors : lightColors;
        const themeWithSavedMode = { ...defaultTheme, theme_mode: savedMode, ...colorOverrides };
        applyTheme(themeWithSavedMode);
      }
      
      const response = await merchantApi.get('/admin/system/theme-settings');
      
      if (response.data.success && response.data.data) {
        const apiSettings = parseThemeFromAPI(response.data.data);
        const mergedSettings = { ...defaultTheme, ...apiSettings };
        
        // Override with localStorage preference if set (colors take precedence)
        if (savedMode) {
          mergedSettings.theme_mode = savedMode;
          const colorOverrides = savedMode === 'dark' ? darkColors : lightColors;
          mergedSettings.bg_main_color = colorOverrides.bg_main_color;
          mergedSettings.text_primary_color = colorOverrides.text_primary_color;
          mergedSettings.text_secondary_color = colorOverrides.text_secondary_color;
        }
        
        applyTheme(mergedSettings as ThemeSettings);
        
        // Parse system settings
        const sysSettings: SystemSettings = {};
        response.data.data.forEach((item: { setting_key: string; setting_value: string }) => {
          if (item.setting_key === 'system_name') sysSettings.system_name = item.setting_value;
          if (item.setting_key === 'system_logo_url') sysSettings.system_logo_url = item.setting_value;
        });
        setSystemSettings(prev => ({ ...prev, ...sysSettings }));
      } else {
        // Use default theme if no settings found
        applyTheme(defaultTheme);
      }
    } catch (err: any) {
      console.warn('Failed to fetch theme settings, using defaults:', err.message);
      // Use default theme on error (don't block the app)
      applyTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  }, [applyTheme]);

  // Update theme settings to API
  const updateTheme = useCallback(async (newSettings: Partial<ThemeSettings>) => {
    try {
      const updatedTheme = { ...theme, ...newSettings };
      
      // Apply immediately for instant feedback
      applyTheme(updatedTheme);
      
      // Save to API
      await merchantApi.post('/admin/system/theme-settings', {
        settings: newSettings
      });
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update theme');
      throw err;
    }
  }, [theme, applyTheme]);

  // Load theme on mount
  useEffect(() => {
    if (localStorage.getItem('theme_mode') !== 'dark') {
      applyCSSVariables(defaultTheme);
    }
    refreshTheme();
  }, [refreshTheme]);

  const value: ThemeContextType = {
    theme,
    systemSettings,
    loading,
    error,
    updateTheme,
    refreshTheme,
    applyTheme,
    toggleTheme,
    isDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================
// Custom Hook
// ============================================
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
